import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useWaitlistCTA } from "../lib/useWaitlistCTA";

const LOGO = "/brand-images/topparent-logo.webp";

const NAV = [
    { to: "/how-it-works", label: "How it works", id: "nav_how_it_works" },
    {
        to: "/neurodiverse-play",
        label: "Children who learn differently",
        id: "nav_neurodiverse_play",
    },
    { to: "/why", label: "Why TopParent", id: "nav_why" },
];

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();
    const { openWaitlist } = useWaitlistCTA();

    // Mobile: close drawer then open the CTA action (modal or scroll).
    const handleMobileCTA = (e) => {
        setOpen(false);
        openWaitlist(e);
    };

    // Close the mobile menu whenever the route changes.
    // setOpen is a stable useState setter, so it does not need to be a dep.
    useEffect(() => setOpen(false), [pathname]);

    // Subscribe to window scroll once on mount.
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-brand-offWhite/85 backdrop-blur-md border-b border-brand-navy/[0.06]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-tp">
                <div className="flex h-16 md:h-20 items-center justify-between">
                    <Link
                        to="/"
                        data-testid="header-logo-link"
                        className="flex items-center gap-2.5 group"
                        aria-label="TopParent home"
                    >
                        <img
                            src={LOGO}
                            alt="TopParent"
                            width="40"
                            height="40"
                            decoding="async"
                            className="h-9 w-9 md:h-10 md:w-10 rounded-full transition-transform group-hover:scale-105"
                        />
                        <span className="font-heading font-bold text-brand-navy text-xl md:text-[22px] tracking-tight">
                            TopParent
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={item.id}
                                className={({ isActive }) =>
                                    `relative px-3.5 py-2 text-[14.5px] font-medium rounded-full transition-colors ${
                                        isActive
                                            ? "text-brand-navy"
                                            : "text-[#334155] hover:text-brand-navy"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <span>{item.label}</span>
                                        {isActive && (
                                            <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] rounded-full bg-brand-coral" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:block">
                        <button
                            type="button"
                            onClick={openWaitlist}
                            data-testid="cta_waitlist"
                            className="btn-primary text-[14.5px] py-2.5 px-5"
                        >
                            Join the waitlist
                        </button>
                    </div>

                    <button
                        type="button"
                        className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full text-brand-navy hover:bg-brand-navy/[0.05] transition-colors"
                        onClick={() => setOpen((s) => !s)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={22} strokeWidth={1.75} /> : <Menu size={22} strokeWidth={1.75} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            <div
                className={`lg:hidden fixed inset-x-0 top-16 md:top-20 bottom-0 z-40 transition-all duration-300 ${
                    open ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
                aria-hidden={!open}
            >
                <div
                    className="absolute inset-0 bg-brand-navy/30 backdrop-blur-sm"
                    onClick={() => setOpen(false)}
                />
                <div
                    className={`absolute top-0 inset-x-0 bg-brand-offWhite border-b border-brand-navy/[0.06] px-5 py-6 transition-transform duration-300 ${
                        open ? "translate-y-0" : "-translate-y-2"
                    }`}
                    data-testid="mobile-menu-drawer"
                >
                    <nav className="flex flex-col gap-1" aria-label="Mobile">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`${item.id}_mobile`}
                                className={({ isActive }) =>
                                    `px-4 py-3.5 text-[17px] rounded-2xl font-medium transition-colors ${
                                        isActive
                                            ? "bg-white text-brand-navy shadow-soft"
                                            : "text-brand-navy/75 hover:bg-white/70"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <button
                            type="button"
                            onClick={handleMobileCTA}
                            data-testid="cta_waitlist_mobile"
                            className="btn-primary mt-4 w-full justify-center"
                        >
                            Join the waitlist
                        </button>
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default Header;
